/**
 * TODO: MAKE THIS INTO AN IndividualsContext or something so it can fetch information about any role
 */
/* eslint-disable @typescript-eslint/camelcase */
import React, { createContext, useCallback, useContext, useState } from "react";

import { EVENT_ID } from "src/shared/constants/event";
import { DEFAULT_MAX_MENTORS } from "src/views/mentor/shift-schedule/constants";
import { useCreateMentorShiftMutation } from "src/views/mentor/shifts/graphql/createMentorShift.generated";

import { BaseShiftState, convertDayToDate } from "../constants";

interface AddMentorShiftState extends BaseShiftState {
  createShift: () => Promise<void>;
}

const DEFAULT_STATE: AddMentorShiftState = {
  shiftName: "",
  shiftDateStart: "",
  shiftDateEnd: "",
  timeStart: "",
  timeEnd: "",
  location: "",
  requiredMentors: DEFAULT_MAX_MENTORS,

  updateShiftName: () => Promise.reject(),
  updateShiftDateStart: () => Promise.reject(),
  updateShiftDateEnd: () => Promise.reject(),
  updateTimeStart: () => Promise.reject(),
  updateTimeEnd: () => Promise.reject(),
  updateLocation: () => Promise.reject(),
  updateRequiredMentors: () => Promise.reject(),

  createShift: () => Promise.reject(),
};

const AddMentorShiftContext: React.Context<AddMentorShiftState> =
  createContext(DEFAULT_STATE);

export const useAddMentorShiftContext = () => useContext(AddMentorShiftContext);

export const AddMentorShiftContextProvider: React.FC = ({ children }) => {
  const [data, setData] = useState<AddMentorShiftState>(() => DEFAULT_STATE);
  const [createShiftMutation] = useCreateMentorShiftMutation();

  const updateShiftName = useCallback((name: string) => {
    setData((prevState) => ({ ...prevState, shiftName: name }));
  }, []);

  const updateShiftDateStart = useCallback((date: string) => {
    setData((prevState) => ({ ...prevState, shiftDateStart: date }));
  }, []);

  const updateShiftDateEnd = useCallback((date: string) => {
    setData((prevState) => ({ ...prevState, shiftDateEnd: date }));
  }, []);

  const updateTimeStart = useCallback((time: string) => {
    setData((prevState) => ({ ...prevState, timeStart: time }));
  }, []);

  const updateTimeEnd = useCallback((time: string) => {
    setData((prevState) => ({ ...prevState, timeEnd: time }));
  }, []);

  const updateLocation = useCallback((location: string) => {
    setData((prevState) => ({ ...prevState, location: location }));
  }, []);

  const updateRequiredMentors = useCallback((mentors: number) => {
    setData((prevState) => ({ ...prevState, requiredMentors: mentors }));
  }, []);

  const createShift = useCallback(async (): Promise<void> => {
    try {
      const dayStart = convertDayToDate(data.shiftDateStart);
      const dayEnd = convertDayToDate(data.shiftDateEnd);

      await createShiftMutation({
        variables: {
          name: data.shiftName,
          start_time: new Date(`${dayStart}T${data.timeStart}:00`),
          end_time: new Date(`${dayEnd}T${data.timeEnd}:00`),
          event_id: EVENT_ID,
          location: data.location,
          max_count: data.requiredMentors,
        },
      });
    } catch (e) {
      console.error(e);
    }
  }, [createShiftMutation, data]);

  const state = {
    ...data,
    updateShiftName,
    updateShiftDateStart,
    updateShiftDateEnd,
    updateTimeStart,
    updateTimeEnd,
    updateLocation,
    updateRequiredMentors,
    createShift,
  };

  return (
    <AddMentorShiftContext.Provider value={state}>
      {children}
    </AddMentorShiftContext.Provider>
  );
};
